import {
  type FocusEvent,
  forwardRef,
  type MouseEvent,
  type Ref,
  type TouchEvent
} from "react";

import { Box, ButtonBase, SxProps, Theme, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  getRegistrationColors,
  getRegistrationFrame
} from "@/src/components/atoms/RegistrationNumber/registrationNumber.styles";
import {
  RegistrationNumberOrientation,
  RegistrationNumberType
} from "@/src/components/atoms/RegistrationNumber/types";

interface RegistrationNumberProps {
  registrationNumber: string;
  orientation?: RegistrationNumberOrientation;
  type?: RegistrationNumberType;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  "aria-describedby"?: string;
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
  onTouchStart?: (event: TouchEvent) => void;
}

const RegistrationNumber = forwardRef<
  HTMLButtonElement | HTMLDivElement,
  RegistrationNumberProps
>((props, ref) => {
  const theme = useTheme();
  const {
    registrationNumber,
    type = "standard",
    orientation = "horizontal",
    sx,
    onClick,
    ...rest
  } = props;

  const verifiedRegistrationNumber = registrationNumber.toUpperCase();

  const { bgColor, fgColor } = getRegistrationColors(theme, type);
  const { borderColor, boxShadow } = getRegistrationFrame(theme);

  let parts: string[] = [];
  if (orientation === "vertical") {
    parts = verifiedRegistrationNumber.split("-");
    if (parts.length < 2) {
      parts = verifiedRegistrationNumber.split(" ");
    }
  }

  const rootSx: SxProps<Theme> = [
    {
      padding: "4px 6px 3px",
      width: "fit-content",
      border: "1px solid",
      borderColor,
      borderRadius: "0.25rem",
      background: bgColor,
      cursor: "default",
      boxShadow,
      // Match Box layout: ButtonBase defaults to inline-flex and would lay out vertical plates in a row.
      display: "block"
    },
    ...(Array.isArray(sx) ? sx : [sx])
  ];

  const content =
    orientation === "vertical" && parts.length === 2 ? (
      <>
        <Typography
          variant="body1"
          sx={{
            fontWeight: theme.typography.fontWeightSemibold,
            color: fgColor,
            lineHeight: "1",
            letterSpacing: "0.07px",
            textAlign: "center",
            textTransform: "uppercase"
          }}
        >
          {parts[0]}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: theme.typography.fontWeightSemibold,
            color: fgColor,
            lineHeight: "1",
            letterSpacing: "0.07px",
            textAlign: "center",
            textTransform: "uppercase"
          }}
        >
          {parts[1]}
        </Typography>
      </>
    ) : (
      <Typography
        variant="body1"
        sx={{
          fontWeight: theme.typography.fontWeightSemibold,
          color: fgColor,
          lineHeight: "1",
          letterSpacing: "0.07px",
          textTransform: "uppercase"
        }}
      >
        {verifiedRegistrationNumber}
      </Typography>
    );

  if (onClick) {
    const handleClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    };
    return (
      <ButtonBase
        ref={ref as Ref<HTMLButtonElement>}
        component="button"
        type="button"
        onClick={handleClick}
        sx={rootSx}
        {...rest}
      >
        {content}
      </ButtonBase>
    );
  }

  return (
    <Box ref={ref as Ref<HTMLDivElement>} sx={rootSx} {...rest}>
      {content}
    </Box>
  );
});

export default RegistrationNumber;
